import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { PALETTE, TUNING } from '@/game/theme';
import { gameBus, GameEvent } from '@/game/events';
import { sfx, unlockAudio } from '@/game/audio';
import { tapFeedback, hitFeedback, successFeedback } from '@/lib/native';
import { createEnvironment, createSkyTexture, createShadowTexture, createTrackMaterial, createMaterials } from '@/game/three/world';
import { createPostFx, type PostFx } from '@/game/three/postfx';
import { QualityGovernor, detectTier, settingsFor, type QualityTier } from '@/game/three/quality';

/**
 * A complete 3D endless runner.
 *
 * It is deliberately whole — lanes, jumping with coyote time and input buffering, pooled hazards,
 * collectibles, power-ups, combo scoring, particles, camera feel, adaptive quality and death —
 * because this file is the reference the real game replaces. Rework the mechanics; keep the SHAPE:
 *
 *   build()   create the world once
 *   update()  advance one frame, driven by delta, never by frame count
 *   dispose() give every geometry, material, texture and render target back
 *
 * Performance rules, all applied below:
 *  - Pool everything. Nothing is allocated inside update().
 *  - Quality adapts to measured frame rate, not just to a device string (see quality.ts).
 *  - Shared geometry and material per repeated element; scenery is a single InstancedMesh.
 *  - Shadows and post-processing are privileges, not assumptions — both can be off at runtime.
 */

interface Pooled {
	mesh: THREE.Object3D;
	active: boolean;
	scored: boolean;
	kind: 'hazard' | 'orb' | 'shield';
}

const laneX = (lane: number): number => (lane - (TUNING.laneCount - 1) / 2) * TUNING.laneWidth;

export class Runner {
	private renderer: THREE.WebGLRenderer;
	private scene = new THREE.Scene();
	private camera: THREE.PerspectiveCamera;
	private clock = new THREE.Clock();
	private raf = 0;

	private tier: QualityTier;
	private governor: QualityGovernor;
	private postfx: PostFx | null = null;

	private environment!: THREE.Texture;
	private materials!: ReturnType<typeof createMaterials>;
	private trackMaterial!: THREE.ShaderMaterial;
	private keyLight!: THREE.DirectionalLight;

	private playerRig = new THREE.Group();
	private player!: THREE.Mesh;
	private playerModel: THREE.Object3D | null = null;
	private shieldBubble!: THREE.Mesh;
	private contactShadow!: THREE.Mesh;
	private trackTiles: THREE.Mesh[] = [];
	private scenery!: THREE.InstancedMesh;
	private sceneryOffsets: number[] = [];

	private pool: Pooled[] = [];
	private sparks!: THREE.Points;
	private sparkVelocities: THREE.Vector3[] = [];
	private sparkLife: number[] = [];
	private speedLines!: THREE.LineSegments;

	private disposables: Array<{ dispose: () => void }> = [];
	private tmpMatrix = new THREE.Matrix4();
	private tmpVec = new THREE.Vector3();
	private tmpQuat = new THREE.Quaternion();
	private tmpScale = new THREE.Vector3(1, 1, 1);

	// --- run state ---
	private running = false;
	private alive = false;
	// Annotated: TUNING is `as const`, so an un-annotated field would inherit the LITERAL type of
	// startSpeed and reject every later assignment.
	private speed: number = TUNING.startSpeed;
	private distance = 0;
	private score = 0;
	private bonus = 0;
	private emittedScore = -1;
	private nextSpawnAt = 0;
	private combo = 0;
	private shielded = false;

	private lane = 1;
	private laneFrom = 1;
	private laneT = 1;
	private y = 0;
	private vy = 0;
	private grounded = true;
	private lastGroundedAt = 0;
	private jumpQueuedAt = -1;
	private now = 0;
	private shake = 0;

	constructor(private container: HTMLElement) {
		this.tier = detectTier();
		const settings = settingsFor(this.tier);

		this.renderer = new THREE.WebGLRenderer({
			antialias: settings.antialias,
			powerPreference: 'high-performance',
		});
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.maxPixelRatio));
		this.renderer.setSize(container.clientWidth, container.clientHeight);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		// ACES is what makes emissive values above 1.0 roll off into colour instead of clipping to white.
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.15;
		container.appendChild(this.renderer.domElement);

		this.camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 140);
		this.camera.position.set(0, 4.3, 10.5);

		this.governor = new QualityGovernor(this.tier, (tier) => this.applyTier(tier));

		this.build();
		this.applyTier(this.tier);

		this.onResize = this.onResize.bind(this);
		window.addEventListener('resize', this.onResize);
	}

	// ---------------------------------------------------------------- world

	private own<T extends { dispose: () => void }>(resource: T): T {
		this.disposables.push(resource);
		return resource;
	}

	private build(): void {
		const settings = settingsFor(this.tier);

		const sky = this.own(createSkyTexture());
		this.scene.background = sky;
		this.scene.fog = new THREE.Fog(new THREE.Color(PALETTE.fog), 18, 78);

		// Image-based lighting: PBR materials need something to reflect, and this is what puts the
		// brand's colours into every highlight rather than leaving surfaces flatly lit.
		this.environment = this.own(createEnvironment(this.renderer));
		this.scene.environment = this.environment;
		this.materials = createMaterials(this.environment);
		for (const material of Object.values(this.materials)) this.own(material);

		const hemi = new THREE.HemisphereLight(0xffd7b0, 0x2a1a3a, 0.5);
		this.scene.add(hemi);

		this.keyLight = new THREE.DirectionalLight(0xffc08a, 2.2);
		this.keyLight.position.set(-5, 11, 5);
		this.keyLight.target.position.set(0, 0, -6);
		// A tight orthographic frustum around the play area: a wide one wastes almost all of the
		// shadow map's resolution on empty ground and gives soft, blocky edges.
		this.keyLight.shadow.camera.left = -9;
		this.keyLight.shadow.camera.right = 9;
		this.keyLight.shadow.camera.top = 9;
		this.keyLight.shadow.camera.bottom = -12;
		this.keyLight.shadow.camera.near = 1;
		this.keyLight.shadow.camera.far = 34;
		this.keyLight.shadow.bias = -0.0015;
		this.scene.add(this.keyLight);
		this.scene.add(this.keyLight.target);

		// Track: long tiles recycled forward, surfaced with the scrolling grid shader.
		this.trackMaterial = this.own(createTrackMaterial());
		const trackGeo = this.own(new THREE.PlaneGeometry(TUNING.laneWidth * TUNING.laneCount, 20, 1, 1));
		for (let i = 0; i < 7; i++) {
			const tile = new THREE.Mesh(trackGeo, this.trackMaterial);
			tile.rotation.x = -Math.PI / 2;
			tile.position.set(0, 0, -i * 20);
			this.scene.add(tile);
			this.trackTiles.push(tile);
		}

		// Scenery as ONE InstancedMesh: 26 rocks in a single draw call instead of 26.
		const sceneryGeo = this.own(new THREE.IcosahedronGeometry(1.6, 0));
		this.scenery = new THREE.InstancedMesh(sceneryGeo, this.materials.scenery, settings.sceneryCount);
		this.scenery.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
		for (let i = 0; i < settings.sceneryCount; i++) {
			const side = i % 2 === 0 ? -1 : 1;
			this.sceneryOffsets.push(-i * 9 - 4);
			this.tmpVec.set(side * (6.5 + Math.random() * 4), -0.6 + Math.random() * 1.4, this.sceneryOffsets[i]);
			this.tmpQuat.setFromEuler(new THREE.Euler(Math.random(), Math.random() * Math.PI, Math.random()));
			this.tmpScale.setScalar(0.7 + Math.random() * 1.5);
			this.tmpMatrix.compose(this.tmpVec, this.tmpQuat, this.tmpScale);
			this.scenery.setMatrixAt(i, this.tmpMatrix);
		}
		this.scenery.instanceMatrix.needsUpdate = true;
		this.scene.add(this.scenery);

		// Player. A rig wraps the mesh so a swapped-in GLTF model animates on the same transform.
		const playerGeo = this.own(new THREE.IcosahedronGeometry(0.5, 3));
		this.player = new THREE.Mesh(playerGeo, this.materials.player);
		this.player.castShadow = true;
		this.playerRig.add(this.player);
		this.playerRig.position.set(laneX(this.lane), 0.75, 0);
		this.scene.add(this.playerRig);

		const shieldGeo = this.own(new THREE.IcosahedronGeometry(0.95, 3));
		this.shieldBubble = new THREE.Mesh(shieldGeo, this.materials.shield);
		this.shieldBubble.visible = false;
		this.playerRig.add(this.shieldBubble);

		// Fake contact shadow. Kept at EVERY quality tier: it is what sells height off the ground, and
		// unlike a shadow map it costs one transparent quad.
		const shadowGeo = this.own(new THREE.PlaneGeometry(1.7, 1.7));
		const shadowTex = this.own(createShadowTexture());
		const shadowMat = this.own(new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
		this.contactShadow = new THREE.Mesh(shadowGeo, shadowMat);
		this.contactShadow.rotation.x = -Math.PI / 2;
		this.contactShadow.position.y = 0.02;
		this.scene.add(this.contactShadow);

		// Pools. Nothing is created after this point.
		const hazardGeo = this.own(new THREE.BoxGeometry(1.25, 1.5, 1.25));
		for (let i = 0; i < 14; i++) {
			const mesh = new THREE.Mesh(hazardGeo, this.materials.hazard);
			mesh.castShadow = true;
			mesh.visible = false;
			this.scene.add(mesh);
			this.pool.push({ mesh, active: false, scored: false, kind: 'hazard' });
		}
		const orbGeo = this.own(new THREE.IcosahedronGeometry(0.28, 1));
		for (let i = 0; i < 20; i++) {
			const mesh = new THREE.Mesh(orbGeo, this.materials.orb);
			mesh.visible = false;
			this.scene.add(mesh);
			this.pool.push({ mesh, active: false, scored: false, kind: 'orb' });
		}
		const shieldPickupGeo = this.own(new THREE.TorusGeometry(0.34, 0.12, 8, 20));
		for (let i = 0; i < 3; i++) {
			const mesh = new THREE.Mesh(shieldPickupGeo, this.materials.orb);
			mesh.visible = false;
			this.scene.add(mesh);
			this.pool.push({ mesh, active: false, scored: false, kind: 'shield' });
		}

		// Particles: one Points cloud recycled for dust, pickups and the death burst.
		const count = settings.particleCount;
		const sparkGeo = this.own(new THREE.BufferGeometry());
		const positions = new Float32Array(count * 3);
		for (let i = 0; i < count; i++) positions[i * 3 + 1] = -100;
		sparkGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
		const sparkMat = this.own(
			new THREE.PointsMaterial({
				color: PALETTE.orbGlow,
				size: 0.16,
				transparent: true,
				opacity: 0.95,
				depthWrite: false,
				blending: THREE.AdditiveBlending,
			}),
		);
		this.sparks = new THREE.Points(sparkGeo, sparkMat);
		this.scene.add(this.sparks);
		for (let i = 0; i < count; i++) {
			this.sparkVelocities.push(new THREE.Vector3());
			this.sparkLife.push(0);
		}

		// Speed lines: streaks either side of the track whose opacity rises with velocity. Cheap, and
		// most of why fast reads as fast rather than merely being fast.
		const lineCount = 40;
		const linePositions = new Float32Array(lineCount * 6);
		for (let i = 0; i < lineCount; i++) {
			const side = i % 2 === 0 ? -1 : 1;
			const x = side * (3.6 + Math.random() * 3);
			const yPos = 0.4 + Math.random() * 4;
			const z = -Math.random() * 60;
			linePositions.set([x, yPos, z, x, yPos, z - 2.2], i * 6);
		}
		const lineGeo = this.own(new THREE.BufferGeometry());
		lineGeo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
		const lineMat = this.own(
			new THREE.LineBasicMaterial({ color: PALETTE.orbGlow, transparent: true, opacity: 0, blending: THREE.AdditiveBlending }),
		);
		this.speedLines = new THREE.LineSegments(lineGeo, lineMat);
		this.scene.add(this.speedLines);
	}

	/**
	 * Swap the placeholder player for a real GLTF model.
	 *
	 * The template cannot ship binary assets, so this is a hook rather than a default: host a .glb
	 * (or import one through the build's asset pipeline) and call this with its URL. On any failure
	 * the procedural mesh stays, so a missing model degrades the look instead of breaking the game.
	 */
	async loadPlayerModel(url: string): Promise<boolean> {
		try {
			const gltf = await new GLTFLoader().loadAsync(url);
			const model = gltf.scene;
			model.traverse((child) => {
				if (child instanceof THREE.Mesh) {
					child.castShadow = true;
					// Give imported materials the scene's environment so they sit in the same light.
					const material = child.material as THREE.MeshStandardMaterial;
					if (material && 'envMap' in material) material.envMap = this.environment;
				}
			});
			this.player.visible = false;
			this.playerModel = model;
			this.playerRig.add(model);
			return true;
		} catch {
			return false;
		}
	}

	// ---------------------------------------------------------------- quality

	private applyTier(tier: QualityTier): void {
		this.tier = tier;
		const settings = settingsFor(tier);
		const { clientWidth: w, clientHeight: h } = this.container;

		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.maxPixelRatio));
		this.renderer.shadowMap.enabled = settings.shadows;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		this.keyLight.castShadow = settings.shadows;
		if (settings.shadows) {
			this.keyLight.shadow.mapSize.set(settings.shadowMapSize, settings.shadowMapSize);
			// Force the shadow map to be rebuilt at the new size.
			this.keyLight.shadow.map?.dispose();
			this.keyLight.shadow.map = null;
		}

		if (settings.postProcessing && !this.postfx) {
			this.postfx = createPostFx(this.renderer, this.scene, this.camera, w, h);
			this.postfx.setSize(w, h, Math.min(window.devicePixelRatio, settings.maxPixelRatio));
		} else if (!settings.postProcessing && this.postfx) {
			this.postfx.dispose();
			this.postfx = null;
		}
	}

	// ---------------------------------------------------------------- input

	/** Tap the upper area to jump; tap left/right of centre to change lane. */
	handlePointer(x: number, y: number, width: number, height: number): void {
		unlockAudio();
		if (!this.alive) return;
		if (y < height * 0.45) return this.queueJump();
		if (x < width * 0.4) this.moveLane(-1);
		else if (x > width * 0.6) this.moveLane(1);
		else this.queueJump();
	}

	handleSwipe(dx: number, dy: number): void {
		unlockAudio();
		if (!this.alive) return;
		if (Math.abs(dy) > Math.abs(dx) && dy < -24) this.queueJump();
		else if (dx > 24) this.moveLane(1);
		else if (dx < -24) this.moveLane(-1);
	}

	handleKey(key: string): void {
		unlockAudio();
		if (!this.alive) return;
		if (key === 'ArrowLeft' || key === 'a') this.moveLane(-1);
		else if (key === 'ArrowRight' || key === 'd') this.moveLane(1);
		else if (key === 'ArrowUp' || key === 'w' || key === ' ') this.queueJump();
	}

	private moveLane(dir: number): void {
		const next = Math.min(TUNING.laneCount - 1, Math.max(0, this.lane + dir));
		if (next === this.lane) return;
		this.laneFrom = this.currentLaneX();
		this.lane = next;
		this.laneT = 0;
		void tapFeedback();
	}

	private queueJump(): void {
		this.jumpQueuedAt = this.now;
		this.tryJump();
	}

	private tryJump(): void {
		if (this.jumpQueuedAt < 0) return;
		if (this.now - this.jumpQueuedAt > TUNING.jumpBufferMs) return;
		if (!this.grounded && this.now - this.lastGroundedAt > TUNING.coyoteTimeMs) return;
		this.vy = TUNING.jumpVelocity;
		this.grounded = false;
		this.jumpQueuedAt = -1;
		sfx.jump();
	}

	private currentLaneX(): number {
		const target = laneX(this.lane);
		if (this.laneT >= 1) return target;
		const t = 1 - Math.pow(1 - this.laneT, 3); // easeOutCubic — decisive, with a settle
		return this.laneFrom + (target - this.laneFrom) * t;
	}

	// ---------------------------------------------------------------- run

	start(): void {
		this.alive = true;
		this.speed = TUNING.startSpeed;
		this.distance = 0;
		this.score = 0;
		this.bonus = 0;
		this.combo = 0;
		this.shielded = false;
		this.shieldBubble.visible = false;
		this.emittedScore = -1;
		this.nextSpawnAt = 14;
		this.lane = 1;
		this.laneFrom = laneX(1);
		this.laneT = 1;
		this.y = 0;
		this.vy = 0;
		this.grounded = true;
		this.shake = 0;
		for (const item of this.pool) {
			item.active = false;
			item.mesh.visible = false;
		}
		gameBus.emit(GameEvent.Score, 0);
		gameBus.emit(GameEvent.Status, 'playing');

		if (!this.running) {
			this.running = true;
			this.clock.start();
			this.loop();
		}
	}

	private loop = (): void => {
		if (!this.running) return;
		this.raf = requestAnimationFrame(this.loop);
		// Clamp delta: a backgrounded tab returns a huge one, which would teleport the player through
		// a hazard on the first frame back.
		const delta = Math.min(this.clock.getDelta(), 0.05);
		this.now = performance.now();
		this.update(delta);

		if (this.postfx) this.postfx.composer.render();
		else this.renderer.render(this.scene, this.camera);

		this.governor.sample(this.now);
	};

	private update(delta: number): void {
		if (this.alive) {
			this.speed = Math.min(TUNING.maxSpeed, TUNING.startSpeed + this.score * TUNING.speedRamp);
			this.distance += this.speed * delta;

			this.score = Math.floor(this.distance / 3) + this.bonus;
			if (this.score !== this.emittedScore) {
				if (this.emittedScore >= 0 && this.score > this.emittedScore && this.score % 10 === 0) sfx.score();
				this.emittedScore = this.score;
				gameBus.emit(GameEvent.Score, this.score);
			}

			this.tryJump();

			this.vy += TUNING.gravity * delta;
			this.y += this.vy * delta;
			if (this.y <= 0) {
				if (!this.grounded) {
					sfx.land();
					this.burst(this.currentLaneX(), 0.12, 0, 6, 0.6);
				}
				this.y = 0;
				this.vy = 0;
				this.grounded = true;
				this.lastGroundedAt = this.now;
			}

			if (this.laneT < 1) this.laneT = Math.min(1, this.laneT + delta * (1000 / TUNING.laneChangeMs));
			this.spawn();
		}

		this.advanceWorld(delta);
		this.updatePlayer(delta);
		this.updateSparks(delta);
		this.updateCamera(delta);

		// Drive the track shader: scroll with distance, brighten with speed.
		const speedT = (this.speed - TUNING.startSpeed) / (TUNING.maxSpeed - TUNING.startSpeed);
		this.trackMaterial.uniforms.uScroll.value = this.distance * 0.05;
		this.trackMaterial.uniforms.uSpeed.value = speedT;
		(this.speedLines.material as THREE.LineBasicMaterial).opacity = Math.max(0, speedT - 0.15) * 0.5;
	}

	private advanceWorld(delta: number): void {
		const dz = this.speed * delta;
		const px = this.currentLaneX();

		for (const tile of this.trackTiles) {
			tile.position.z += dz;
			if (tile.position.z > 20) tile.position.z -= 140;
		}

		// Scenery instances scroll by rewriting their matrices — still one draw call.
		for (let i = 0; i < this.sceneryOffsets.length; i++) {
			this.sceneryOffsets[i] += dz * 0.9;
			if (this.sceneryOffsets[i] > 14) this.sceneryOffsets[i] -= this.sceneryOffsets.length * 9;
			this.scenery.getMatrixAt(i, this.tmpMatrix);
			this.tmpMatrix.decompose(this.tmpVec, this.tmpQuat, this.tmpScale);
			this.tmpVec.z = this.sceneryOffsets[i];
			this.tmpMatrix.compose(this.tmpVec, this.tmpQuat, this.tmpScale);
			this.scenery.setMatrixAt(i, this.tmpMatrix);
		}
		this.scenery.instanceMatrix.needsUpdate = true;

		const linePositions = this.speedLines.geometry.getAttribute('position') as THREE.BufferAttribute;
		for (let i = 0; i < linePositions.count; i += 2) {
			const z = linePositions.getZ(i) + dz * 1.6;
			if (z > 12) {
				linePositions.setZ(i, -60);
				linePositions.setZ(i + 1, -62.2);
			} else {
				linePositions.setZ(i, z);
				linePositions.setZ(i + 1, z - 2.2);
			}
		}
		linePositions.needsUpdate = true;

		for (const item of this.pool) {
			if (!item.active) continue;
			item.mesh.position.z += dz;

			if (item.kind === 'hazard') {
				item.mesh.rotation.y += delta * 0.5;
				if (this.alive) {
					const dx = Math.abs(item.mesh.position.x - px);
					const dzAbs = Math.abs(item.mesh.position.z);
					if (dx < 0.92 && dzAbs < 0.92 && this.y < 1.2) {
						this.hit(item);
					} else if (!item.scored && item.mesh.position.z > 1.2) {
						item.scored = true;
						if (dx < TUNING.nearMissRadius) {
							this.bonus += TUNING.nearMissBonus;
							sfx.nearMiss();
							gameBus.emit(GameEvent.NearMiss, TUNING.nearMissBonus);
							this.burst(item.mesh.position.x, 1, item.mesh.position.z, 8, 1.5);
						}
					}
				}
			} else {
				item.mesh.rotation.x += delta * 2.2;
				item.mesh.rotation.y += delta * 1.7;
				if (this.alive && !item.scored) {
					const dx = Math.abs(item.mesh.position.x - px);
					const dy = Math.abs(item.mesh.position.y - (this.y + 0.75));
					const dzAbs = Math.abs(item.mesh.position.z);
					if (dx < 0.85 && dzAbs < 0.85 && dy < 1.15) this.collect(item);
				}
			}

			if (item.mesh.position.z > 6) {
				item.active = false;
				item.mesh.visible = false;
			}
		}
	}

	private collect(item: Pooled): void {
		item.scored = true;
		item.active = false;
		item.mesh.visible = false;

		if (item.kind === 'shield') {
			this.shielded = true;
			this.shieldBubble.visible = true;
			void successFeedback();
			sfx.nearMiss();
		} else {
			// Combo: consecutive orbs are worth progressively more, so a clean line beats a lucky one.
			this.combo = Math.min(5, this.combo + 1);
			this.bonus += TUNING.orbValue * this.combo;
			sfx.score();
			if (this.combo > 1) gameBus.emit(GameEvent.NearMiss, TUNING.orbValue * this.combo);
		}
		this.burst(item.mesh.position.x, item.mesh.position.y, item.mesh.position.z, 12, 2.2);
	}

	private hit(item: Pooled): void {
		if (this.shielded) {
			// A shield absorbs one hit and clears the hazard, rather than ending the run.
			this.shielded = false;
			this.shieldBubble.visible = false;
			item.active = false;
			item.mesh.visible = false;
			this.combo = 0;
			this.shake = 0.3;
			void hitFeedback();
			this.burst(item.mesh.position.x, 1, item.mesh.position.z, 18, 3);
			return;
		}
		this.die();
	}

	/** Spawn a readable pattern: never block every lane, and put a reward in the clean line. */
	private spawn(): void {
		if (this.distance < this.nextSpawnAt) return;
		const gap = Math.max(TUNING.spawnEveryMinM, TUNING.spawnEveryM - this.score * 0.03);
		this.nextSpawnAt = this.distance + gap;

		const safeLane = Math.floor(Math.random() * TUNING.laneCount);
		const blockCount = this.score > 40 && Math.random() < 0.45 ? 2 : 1;
		let placed = 0;
		for (let lane = 0; lane < TUNING.laneCount && placed < blockCount; lane++) {
			if (lane === safeLane) continue;
			const hazard = this.pool.find((p) => p.kind === 'hazard' && !p.active);
			if (!hazard) break;
			hazard.active = true;
			hazard.scored = false;
			hazard.mesh.visible = true;
			hazard.mesh.position.set(laneX(lane), 0.75, -70);
			placed++;
		}

		// A shield appears occasionally once the game is genuinely hard.
		if (this.score > 25 && Math.random() < 0.12) {
			const shield = this.pool.find((p) => p.kind === 'shield' && !p.active);
			if (shield) {
				shield.active = true;
				shield.scored = false;
				shield.mesh.visible = true;
				shield.mesh.position.set(laneX(safeLane), 1.1, -70);
				return;
			}
		}

		if (Math.random() < 0.8) {
			const orb = this.pool.find((p) => p.kind === 'orb' && !p.active);
			if (orb) {
				orb.active = true;
				orb.scored = false;
				orb.mesh.visible = true;
				orb.mesh.position.set(laneX(safeLane), Math.random() < 0.3 ? 2.1 : 0.9, -70 - Math.random() * 6);
			}
		}
	}

	private updatePlayer(delta: number): void {
		const x = this.currentLaneX();
		this.playerRig.position.x = x;
		this.playerRig.position.y = 0.75 + this.y;

		const spin = this.playerModel ?? this.player;
		spin.rotation.x -= delta * (this.speed * 0.3);
		// Bank into a lane change: motion that reads as intent rather than drift.
		const targetZ = (laneX(this.lane) - x) * 0.22;
		this.playerRig.rotation.z += (targetZ - this.playerRig.rotation.z) * Math.min(1, delta * 12);

		if (this.shieldBubble.visible) {
			this.shieldBubble.rotation.y += delta * 1.4;
			this.shieldBubble.scale.setScalar(1 + Math.sin(this.now * 0.005) * 0.04);
		}

		this.contactShadow.position.x = x;
		const height = Math.min(1, this.y / 4);
		this.contactShadow.scale.setScalar(1 - height * 0.5);
		(this.contactShadow.material as THREE.MeshBasicMaterial).opacity = 1 - height * 0.8;

		// Keep the shadow frustum on the player, so a tight map stays where it is needed.
		this.keyLight.position.x = x - 5;
		this.keyLight.target.position.set(x, 0, -6);
		this.keyLight.target.updateMatrixWorld();
	}

	/** Emit `count` particles from a point. Reuses slots; never allocates. */
	private burst(x: number, y: number, z: number, count: number, power: number): void {
		const positions = this.sparks.geometry.getAttribute('position') as THREE.BufferAttribute;
		let emitted = 0;
		for (let i = 0; i < this.sparkLife.length && emitted < count; i++) {
			if (this.sparkLife[i] > 0) continue;
			positions.setXYZ(i, x, y, z);
			this.sparkVelocities[i].set(
				(Math.random() - 0.5) * power,
				Math.random() * power * 0.9 + 0.4,
				(Math.random() - 0.5) * power,
			);
			this.sparkLife[i] = 0.5 + Math.random() * 0.35;
			emitted++;
		}
		positions.needsUpdate = true;
	}

	private updateSparks(delta: number): void {
		const positions = this.sparks.geometry.getAttribute('position') as THREE.BufferAttribute;
		let dirty = false;
		for (let i = 0; i < this.sparkLife.length; i++) {
			if (this.sparkLife[i] <= 0) continue;
			this.sparkLife[i] -= delta;
			const v = this.sparkVelocities[i];
			v.y += TUNING.gravity * 0.35 * delta;
			positions.setXYZ(
				i,
				positions.getX(i) + v.x * delta,
				positions.getY(i) + v.y * delta,
				positions.getZ(i) + v.z * delta + this.speed * delta,
			);
			if (this.sparkLife[i] <= 0) positions.setXYZ(i, 0, -100, 0);
			dirty = true;
		}
		if (dirty) positions.needsUpdate = true;
	}

	private updateCamera(delta: number): void {
		// The camera trails rather than tracks, and the FOV opens with speed. Both are small; together
		// they are most of why fast feels fast.
		const targetX = this.currentLaneX() * 0.5;
		this.camera.position.x += (targetX - this.camera.position.x) * Math.min(1, delta * 6);
		this.camera.position.y = 4.3 + this.y * 0.14;
		this.camera.position.z = 10.5;

		const speedT = (this.speed - TUNING.startSpeed) / (TUNING.maxSpeed - TUNING.startSpeed);
		this.camera.fov += (55 + speedT * 8 - this.camera.fov) * Math.min(1, delta * 2);

		if (this.shake > 0) {
			this.shake = Math.max(0, this.shake - delta * 2.2);
			this.camera.position.x += (Math.random() - 0.5) * this.shake;
			this.camera.position.y += (Math.random() - 0.5) * this.shake;
		}
		this.camera.updateProjectionMatrix();
		this.camera.lookAt(this.currentLaneX() * 0.3, 1.0 + this.y * 0.2, -18);
	}

	private die(): void {
		if (!this.alive) return;
		this.alive = false;
		this.shake = 0.6;
		this.combo = 0;
		sfx.death();
		void hitFeedback();
		this.burst(this.playerRig.position.x, this.playerRig.position.y, 0, 28, 4.5);
		gameBus.emit(GameEvent.Status, 'over');
	}

	// ---------------------------------------------------------------- lifecycle

	pause(): void {
		if (!this.running) return;
		this.running = false;
		cancelAnimationFrame(this.raf);
	}

	resume(): void {
		if (this.running) return;
		this.running = true;
		this.clock.getDelta();
		this.loop();
	}

	restart(): void {
		this.start();
	}

	private onResize(): void {
		const { clientWidth: w, clientHeight: h } = this.container;
		if (!w || !h) return;
		const settings = settingsFor(this.tier);
		const pixelRatio = Math.min(window.devicePixelRatio, settings.maxPixelRatio);
		this.renderer.setSize(w, h);
		this.camera.aspect = w / h;
		this.camera.updateProjectionMatrix();
		this.postfx?.setSize(w, h, pixelRatio);
	}

	dispose(): void {
		this.running = false;
		cancelAnimationFrame(this.raf);
		window.removeEventListener('resize', this.onResize);
		this.postfx?.dispose();
		this.playerModel?.traverse((child) => {
			if (child instanceof THREE.Mesh) {
				child.geometry.dispose();
				const material = child.material;
				if (Array.isArray(material)) material.forEach((m) => m.dispose());
				else material?.dispose();
			}
		});
		for (const resource of this.disposables) resource.dispose();
		this.scenery.dispose();
		this.renderer.dispose();
		this.renderer.domElement.remove();
	}
}
